import * as React from 'react'
import { bind } from 'classnames/bind'
import * as styles from '@procore/core-css/src/components/_dropdown.scss'

import { PlacementNS, Omit } from '../utils/types'
import Button, { ButtonProps, ButtonVariantNS } from './Button'
import Card from './Card'
import Menu, { Footer, Options, Search, Selection, isItem } from './Menu'
import { OverlayTriggerAdapter as OverlayTrigger } from './OverlayTriggerAdapter'
import { useSearch } from './Search'
import { consumeOverlayTrigger } from './OverlayTriggerNext'

const cx = bind(styles)

type unused = any

export type DropdownPlacement =
  | typeof PlacementNS.bottom
  | typeof PlacementNS.bottomLeft
  | typeof PlacementNS.bottomRight
  | typeof PlacementNS.top
  | typeof PlacementNS.topLeft
  | typeof PlacementNS.topRight

interface DropdownProps extends Omit<ButtonProps, 'onSelect'> {
  footer?: React.ReactNode
  label?: string
  onSearch?: (event: React.ChangeEvent<HTMLInputElement>) => unused
  onSelect?: (selection: Selection) => any
  onVisibleChange?: (isVisible: boolean, e: Event) => boolean | undefined
  placement?: DropdownPlacement
}

interface ConsumerProps {
  children: (api: any) => {}
}

const Dropdown = React.forwardRef<HTMLButtonElement, DropdownProps>(
  (
    {
      children,
      error,
      footer,
      icon,
      label,
      onSearch,
      onSelect = () => {},
      onVisibleChange = (isVisible, e) => true,
      placement = PlacementNS.bottomLeft,
      variant = ButtonVariantNS.dropdown,
      ...props
    },
    ref
  ) => {
    const refSearch = React.useRef(null)
    const search = useSearch({ onChange: onSearch, ref: refSearch })

    let initialIndex = 0
    if (search.empty) {
      initialIndex = React.Children.toArray(children)
        .filter(isItem)
        .findIndex((node: any) => node && node.props && node.props.suggested)
      initialIndex = initialIndex === -1 ? 0 : initialIndex
    }

    return (
      <OverlayTrigger
        autoFocus={!onSearch}
        container={Card}
        containerClass={cx('core-dropdown__overlay')}
        onVisibleChange={(isVisible, e) => {
          const shouldToggle = onVisibleChange(isVisible, e) !== false
          if (shouldToggle && !isVisible) {
            search.clear()
          }
          return shouldToggle
        }}
        overlay={
          <Menu initialIndex={initialIndex} onSelect={onSelect} scrollable>
            {onSearch && (
              <Search
                ref={refSearch}
                autoComplete="false"
                autoFocus
                value={search.value}
                onClear={search.clear}
                onChange={search.onChange}
              />
            )}
            <Options>{children}</Options>
            {footer && <Footer>{footer}</Footer>}
          </Menu>
        }
        placement={placement}
        ref={ref}
      >
        <Button
          {...props}
          dropdown={!!label}
          error={error}
          variant={variant}
          icon={icon}
        >
          {label}
        </Button>
      </OverlayTrigger>
    )
  }
)

export const consumeDropdown = consumeOverlayTrigger

export const DropdownConsumer = ({ children }: ConsumerProps) =>
  children(consumeDropdown())

export default Dropdown
